import React, { MutableRefObject, useEffect } from "react";
import toast from "react-hot-toast";
import { CreditCard, PaymentForm } from "react-square-web-payments-sdk";
import { useSquarePayment } from "@1-now/booking-core";
import type { PaymentError, SquarePaid } from "@1-now/booking-core";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import UnconfirmedNotice from "./UnconfirmedNotice";
import type { PayState } from "./CheckOutPage";

// 1NOW-10711: one id per Pay & Book click, sent with the charge so the backend
// (and Square) can tell a replay of this click from a deliberate new attempt.
const newRequestId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

interface SquarePaymentFormProps {
  squareDetails: any; // Replace with actual type if available
  /** The money is on the booking. See SquarePaid in @1-now/booking-core. */
  onPaid: (paid: SquarePaid) => void;
  /** A payment that did not go through, once per attempt (1NOW-11860). See PaymentError. */
  onPaymentError?: (failure: PaymentError) => void;
  amount: number;
  rentalAgreementChecked: boolean;
  privacyPolicyCheckedChecked: boolean;
  termsandconditionChecked: boolean;
  /** 1NOW-11155: the phone booking's bar presses Pay (payRef) instead of this form's own button. */
  embedded?: boolean;
  payRef?: MutableRefObject<(() => void) | null>;
  onPayState?: (state: PayState) => void;
}

const SquarePaymentForm: React.FC<SquarePaymentFormProps> = ({
  squareDetails,
  onPaid,
  onPaymentError,
  amount,
  rentalAgreementChecked = false,
  privacyPolicyCheckedChecked = false,
  termsandconditionChecked = false,
  embedded = false,
  payRef,
  onPayState,
}) => {
  /**
   * Save the card, charge the booking, and read the booking back after a
   * charge that fails: the shared payment step, as the Stripe form and Design
   * C use it (useSquarePayment in @1-now/booking-core). One attempt at a time
   * (1NOW-10668), a request id per attempt (1NOW-10711), and a card that
   * cannot be saved stops with its reason (1NOW-10669).
   */
  const square = useSquarePayment({ onPaid, onPaymentError });

  // Replace the default Square button after the PaymentForm mounts
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = "#rswp-card-button { display: none !important; }";
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleCustomPayClick = () => {
    if (termsandconditionChecked !== true) {
      toast.error("Please agree to the Terms and Conditions");
      return;
    }
    if (rentalAgreementChecked !== true) {
      toast.error("Please review and accept the Rental Agreement");
      return;
    }
    if (privacyPolicyCheckedChecked !== true) {
      toast.error("Please agree to Privacy & Cancellation Policies");
      return;
    }
    const squareBtn = document.getElementById("rswp-card-button");
    if (squareBtn) {
      squareBtn.click();
    }
  };

  const payLabel = square.locked ? "Payment not confirmed yet" : "Pay & Book";

  useEffect(() => {
    if (!payRef) return;
    payRef.current = handleCustomPayClick;
    return () => {
      payRef.current = null;
    };
  });
  useEffect(() => {
    onPayState?.({ busy: square.loading, locked: square.locked });
  }, [square.loading, square.locked]);

  return (
    <div className="mt-8 pt-8">
      <span className="text-lg text-text-color font-semibold mt-4">
        Payment Information
      </span>
      <div className="mb-6"></div>
      <PaymentForm
        applicationId={squareDetails?.application_id} // Replace with your app ID
        locationId={squareDetails?.location_id} // Replace with your location ID
        cardTokenizeResponseReceived={(result) => square.pay(result)}
        createPaymentRequest={() => ({
          countryCode: "US",
          currencyCode: "USD",
          total: {
            amount: amount.toString(),
            label: "Total",
          },
        })}
      >
        <CreditCard />
        {/* <ApplePay /> <GooglePay /> */}
      </PaymentForm>
      {!embedded && (
      <ButtonPrimary
        type="button"
        className="w-full mt-4 rounded-md"
        onClick={handleCustomPayClick}
        disabled={square.loading || square.locked}
        aria-label={payLabel}
      >
        {square.loading ? (
          <div className="flex justify-center items-center col-span-full">
            <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-secondary-100"></div>
          </div>
        ) : (
          payLabel
        )}
      </ButtonPrimary>
      )}
      {square.unconfirmed && (
        <UnconfirmedNotice
          state={square.unconfirmed}
          checking={square.checking}
          onCheck={square.checkPayment}
        />
      )}
    </div>
  );
};

export default SquarePaymentForm;
